import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/components/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import CalendarPage from "@/pages/CalendarPage";
import AssignmentsPage from "@/pages/AssignmentsPage";
import CoursesPage from "@/pages/CoursesPage";
import SettingsPage from "@/pages/SettingsPage";
import UploadFlow from "@/pages/UploadFlow";
import Upgrade from "@/pages/Upgrade";
import PaymentResult from "@/pages/PaymentResult";
import { Privacy, Terms } from "@/pages/LegalPages";
import BecomeAmbassador from "@/pages/BecomeAmbassador";
import Leaderboard from "@/pages/Leaderboard";
import AmbassadorDashboard from "@/pages/AmbassadorDashboard";
import AdminPanel from "@/pages/AdminPanel";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/become-ambassador" element={<BecomeAmbassador />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/upload" element={<ProtectedRoute><UploadFlow /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardHome />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="assignments" element={<AssignmentsPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="ambassador" element={<AmbassadorDashboard />} />
        <Route path="admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
